import { BaseProductsClient } from "../types/clients";
import {
  Parhato2ProductSubscription,
  Product,
  ProductResults,
  ProductReview,
  ProductReviewPayload,
  ProductsQueryParams,
  SearchProductsQueryParams,
} from "@core/types";
import { ParhatoProduct, ParhatoProductReview } from "./types";
import { getCurrentExpressPointId } from "./utils";
import WooComAPI from "../../../../providers/WooComAPI/woo-com-api";
import { convertParhatoProduct, convertParhatoReview } from "./converters";
import { SupaProducts } from "../types/supabaseFragments";

const DEFAULT_PRODUCTS_PARAMS = {
  status: "publish",
  stock_status: "instock",
  min_price: 0.1,
  per_page: 21,
};

function convertProductsProps(props: ProductsQueryParams) {
  return {
    on_sale: props.saleType,
    page: props.pageNumber,
    per_page: props.pageSize,
    category: props.categoryId,
    orderby: props.sortOrder,
    min_price: props.minPrice,
    max_price: props.maxPrice,
    keyword: props.keyword,
    featured: props.featured,
  };
}

class ProductsClient extends BaseProductsClient {
  async getProducts(productsQueryParams: ProductsQueryParams): Promise<ProductResults> {
    const response = await this.instance.get<SupaProducts[]>("/wp-json/wc/v3/products?", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        ...convertProductsProps(productsQueryParams),
        shippingZone: productsQueryParams.zoneId,
        expressPointId: getCurrentExpressPointId(),
      },
    });

    return {
      products: response.data?.map((product) => convertParhatoProduct(product as any as ParhatoProduct)),
      additionalFeatures: [],
    };
  }

  async searchProducts(params: SearchProductsQueryParams, zoneId?: string) {
    const response = await this.instance.get<ParhatoProduct[]>("/wp-json/parhato-api/v1/search-products?", {
      params: {
        q: params.keyword,
        page: params.pageNumber,
        shippingZone: zoneId,
        additionalFeature: params.additionalFeature,
        expressPointId: getCurrentExpressPointId(),
      },
    });

    return response.data.map(convertParhatoProduct);
  }

  async getCustomersChoiceProducts(productsQueryParams: ProductsQueryParams): Promise<ProductResults> {
    const response = await this.instance.get<ParhatoProduct[]>("wp-json/wc/v3/products", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        shippingZone: productsQueryParams.zoneId,
        expressPointId: getCurrentExpressPointId(),
        orderby: "popularity",
        order: "desc",
      },
    });

    return {
      products: response.data.map(convertParhatoProduct),
      additionalFeatures: [],
    };
  }

  async getNewestProducts(productsQueryParams: ProductsQueryParams): Promise<ProductResults> {
    const response = await this.instance.get<ParhatoProduct[]>("/wp-json/wc/v3/products", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        shippingZone: productsQueryParams.zoneId,
        expressPointId: getCurrentExpressPointId(),
      },
    });
    return {
      products: response.data.map(convertParhatoProduct),
      additionalFeatures: [],
    };
  }

  async getOnSaleProducts(productsQueryParams: ProductsQueryParams): Promise<ProductResults> {
    const response = await this.instance.get<ParhatoProduct[]>("/wp-json/wc/v3/products", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        shippingZone: productsQueryParams.zoneId,
        expressPointId: getCurrentExpressPointId(),
        on_sale: true,
      },
    });

    return {
      products: response.data.map(convertParhatoProduct),
      additionalFeatures: [],
    };
  }

  async getFeaturedProducts(productsQueryParams: ProductsQueryParams): Promise<ProductResults> {
    const response = await this.instance.get<ParhatoProduct[]>("/wp-json/wc/v3/products", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        shippingZone: productsQueryParams.zoneId,
        expressPointId: getCurrentExpressPointId(),
        featured: true,
      },
    });

    return {
      products: response.data.map(convertParhatoProduct),
      additionalFeatures: [],
    };
  }

  async getProductById(id: string, zoneId?: string): Promise<Product> {
    const response = await this.instance.get<ParhatoProduct>(`/wp-json/wc/v3/products/${id}`, {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        shippingZone: zoneId,
        expressPointId: getCurrentExpressPointId(),
      },
    });

    return convertParhatoProduct(response.data);
  }

  async getIncludeProducts(include: string[], params: { per_page?: string; zoneId: string }) {
    const response = await this.instance.get<ParhatoProduct[]>("/wp-json/wc/v3/products", {
      params: {
        include: include.join(","),
        per_page: params.per_page ? params.per_page : include.length + 1,
        shippingZone: params.zoneId,
        expressPointId: getCurrentExpressPointId(),
      },
    });

    return response.data.map(convertParhatoProduct);
  }

  async getProductReviews(id: Product["id"]): Promise<ProductReview[]> {
    const response = await this.instance.get<ParhatoProductReview[]>("/wp-json/wc/v3/products/reviews?product=" + id);

    return response.data.map(convertParhatoReview);
  }

  async addProductReview(productReviewPayload: ProductReviewPayload): Promise<ProductReview> {
    const review = await WooComAPI.postAsync("products/reviews", productReviewPayload);
    // @ts-ignore
    return convertParhatoReview(review);
  }

  async addProductSubscription(productId: Product["id"]): Promise<Parhato2ProductSubscription | null> {
    throw new Error("addProductSubscription not implemented");
  }

  async getAllProductsSubscriptions(): Promise<Parhato2ProductSubscription[]> {
    throw new Error("getAllProductsSubscriptions not implemented");
  }

  async getProductsFromParentAndChildCategories(params: ProductsQueryParams): Promise<Product[]> {
    throw new Error("getProductsFromParentAndChildCategories not implemented");
  }

  async getAdditionalProductFilters(): Promise<any[]> {
    throw new Error("getAdditionalProductFilters not implemented");
  }
}
export default ProductsClient;
